import React, { useState, useEffect, useRef } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "../../utils";
import { 
  Search, X, Loader2, Package, Wallet, TrendingUp, TrendingDown, 
  Wrench, Wheat, Receipt, FileText, Landmark, Warehouse
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";

const entityConfig = {
  lancamentos: { 
    icon: Wallet, 
    label: "Lançamento", 
    color: "bg-blue-100 text-blue-700",
    page: "Financeiro",
    getTitle: (item) => item.descricao || item.categoria,
    getSubtitle: (item) => `${item.tipo === 'receita' ? 'Receita' : 'Despesa'} • ${item.data ? format(parseISO(item.data), 'dd/MM/yyyy') : ''}`
  },
  contasPagar: { 
    icon: TrendingDown, 
    label: "Conta a Pagar", 
    color: "bg-red-100 text-red-700",
    page: "ContasPagar",
    getTitle: (item) => item.descricao,
    getSubtitle: (item) => `${item.fornecedor || ''} • Venc: ${item.data_vencimento ? format(parseISO(item.data_vencimento), 'dd/MM/yyyy') : ''}`
  },
  contasReceber: { 
    icon: TrendingUp, 
    label: "Conta a Receber", 
    color: "bg-green-100 text-green-700",
    page: "ContasReceber",
    getTitle: (item) => item.descricao,
    getSubtitle: (item) => `${item.cliente || ''} • Venc: ${item.data_vencimento ? format(parseISO(item.data_vencimento), 'dd/MM/yyyy') : ''}`
  },
  ativos: { 
    icon: Package, 
    label: "Ativo", 
    color: "bg-purple-100 text-purple-700",
    page: "Ativos",
    getTitle: (item) => item.nome,
    getSubtitle: (item) => `${item.marca || ''} ${item.modelo || ''} • ${item.numero_serie || ''}`
  },
  manutencoes: { 
    icon: Wrench, 
    label: "Manutenção", 
    color: "bg-amber-100 text-amber-700",
    page: "Ativos",
    getTitle: (item) => item.descricao,
    getSubtitle: (item) => `${item.tipo} • ${item.data_manutencao ? format(parseISO(item.data_manutencao), 'dd/MM/yyyy') : ''}`
  },
  safras: { 
    icon: Wheat, 
    label: "Safra", 
    color: "bg-emerald-100 text-emerald-700",
    page: "Safras",
    getTitle: (item) => item.nome,
    getSubtitle: (item) => `${item.cultura || ''} • ${item.area_plantada ? item.area_plantada + ' ha' : ''}`
  },
  financiamentos: { 
    icon: Landmark, 
    label: "Financiamento", 
    color: "bg-indigo-100 text-indigo-700",
    page: "Financiamentos",
    getTitle: (item) => `${item.instituicao} - ${item.numero_contrato || ''}`,
    getSubtitle: (item) => `${item.tipo} • ${item.finalidade || ''}`
  },
  estoqueGraos: { 
    icon: Warehouse, 
    label: "Estoque Grão", 
    color: "bg-amber-100 text-amber-700",
    page: "ArmazensGraos",
    getTitle: (item) => item.produto,
    getSubtitle: (item) => `${item.quantidade?.toLocaleString('pt-BR') || 0} ${item.unidade || 'sacas'} • Lote: ${item.lote || '-'}`
  }
};

export default function BuscaGlobal({ open, onOpenChange }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const inputRef = useRef(null);
  const propriedadeId = localStorage.getItem("propriedade_selecionada");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setSearchTerm("");
    }
  }, [open]);

  // Buscar em todas as entidades
  const { data: results = [], isLoading } = useQuery({
    queryKey: ['busca_global', debouncedTerm, propriedadeId],
    queryFn: async () => {
      if (!debouncedTerm || debouncedTerm.length < 2 || !propriedadeId) return [];
      
      const term = debouncedTerm.toLowerCase();
      const allResults = [];

      // Buscar em paralelo
      const [lancamentos, contasPagar, contasReceber, ativos, manutencoes, safras, financiamentos, estoqueGraos] = await Promise.all([
        agrogestao.entities.Lancamento.filter({ propriedade_id: propriedadeId }, '-created_date', 100),
        agrogestao.entities.ContaPagar.filter({ propriedade_id: propriedadeId }, '-created_date', 100),
        agrogestao.entities.ContaReceber.filter({ propriedade_id: propriedadeId }, '-created_date', 100),
        agrogestao.entities.AtivoImobilizado.filter({ propriedade_id: propriedadeId }),
        agrogestao.entities.ManutencaoAtivo.filter({ propriedade_id: propriedadeId }, '-data_manutencao', 100),
        agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId }),
        agrogestao.entities.Financiamento.filter({ propriedade_id: propriedadeId }),
        agrogestao.entities.EstoqueGrao.filter({ propriedade_id: propriedadeId })
      ]);

      // Filtrar lançamentos
      lancamentos.filter(l => 
        l.descricao?.toLowerCase().includes(term) || 
        l.categoria?.toLowerCase().includes(term) ||
        l.fornecedor_cliente?.toLowerCase().includes(term)
      ).slice(0, 5).forEach(item => allResults.push({ ...item, _type: 'lancamentos' }));

      // Filtrar contas a pagar
      contasPagar.filter(c => 
        c.descricao?.toLowerCase().includes(term) || 
        c.fornecedor?.toLowerCase().includes(term) ||
        c.categoria?.toLowerCase().includes(term)
      ).slice(0, 5).forEach(item => allResults.push({ ...item, _type: 'contasPagar' }));

      // Filtrar contas a receber
      contasReceber.filter(c => 
        c.descricao?.toLowerCase().includes(term) || 
        c.cliente?.toLowerCase().includes(term) ||
        c.categoria?.toLowerCase().includes(term)
      ).slice(0, 5).forEach(item => allResults.push({ ...item, _type: 'contasReceber' }));

      // Filtrar ativos
      ativos.filter(a => 
        a.nome?.toLowerCase().includes(term) || 
        a.marca?.toLowerCase().includes(term) ||
        a.modelo?.toLowerCase().includes(term) ||
        a.numero_serie?.toLowerCase().includes(term) ||
        a.placa?.toLowerCase().includes(term)
      ).slice(0, 5).forEach(item => allResults.push({ ...item, _type: 'ativos' }));

      // Filtrar manutenções
      manutencoes.filter(m => 
        m.descricao?.toLowerCase().includes(term) ||
        m.fornecedor_servico?.toLowerCase().includes(term)
      ).slice(0, 5).forEach(item => allResults.push({ ...item, _type: 'manutencoes' }));

      // Filtrar safras
      safras.filter(s => 
        s.nome?.toLowerCase().includes(term) || 
        s.cultura?.toLowerCase().includes(term)
      ).slice(0, 5).forEach(item => allResults.push({ ...item, _type: 'safras' }));

      // Filtrar financiamentos
      financiamentos.filter(f => 
        f.instituicao?.toLowerCase().includes(term) || 
        f.numero_contrato?.toLowerCase().includes(term) ||
        f.finalidade?.toLowerCase().includes(term)
      ).slice(0, 5).forEach(item => allResults.push({ ...item, _type: 'financiamentos' }));

      // Filtrar estoque de grãos
      estoqueGraos.filter(e => 
        e.produto?.toLowerCase().includes(term) || 
        e.lote?.toLowerCase().includes(term)
      ).slice(0, 5).forEach(item => allResults.push({ ...item, _type: 'estoqueGraos' }));

      return allResults;
    },
    enabled: !!debouncedTerm && debouncedTerm.length >= 2 && !!propriedadeId
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const groupedResults = results.reduce((acc, item) => {
    const type = item._type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl p-0 gap-0">
        <DialogDescription className="sr-only">Pesquise rapidamente dados e páginas</DialogDescription>
        <div className="flex items-center border-b border-stone-200 px-4">
          <Search className="w-5 h-5 text-stone-400" />
          <Input
            ref={inputRef}
            placeholder="Buscar lançamentos, contas, ativos, safras..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-0 focus-visible:ring-0 text-lg py-6"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")} className="p-1 hover:bg-stone-100 rounded">
              <X className="w-4 h-4 text-stone-400" />
            </button>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
            </div>
          ) : !searchTerm || searchTerm.length < 2 ? (
            <div className="text-center py-12 text-stone-500">
              <Search className="w-10 h-10 mx-auto mb-3 text-stone-300" />
              <p>Digite ao menos 2 caracteres para buscar</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <p>Nenhum resultado encontrado</p>
            </div>
          ) : (
            <div className="py-2">
              {Object.entries(groupedResults).map(([type, items]) => {
                const config = entityConfig[type];
                const Icon = config.icon;
                
                return (
                  <div key={type} className="mb-2">
                    <div className="px-4 py-2 text-xs font-semibold text-stone-500 uppercase bg-stone-50">
                      {config.label}s ({items.length})
                    </div>
                    {items.map(item => (
                      <Link
                        key={item.id}
                        to={createPageUrl(config.page)}
                        onClick={() => onOpenChange(false)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors"
                      >
                        <div className={`p-2 rounded-lg ${config.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-800 truncate">{config.getTitle(item)}</p>
                          <p className="text-sm text-stone-500 truncate">{config.getSubtitle(item)}</p>
                        </div>
                        {item.valor && (
                          <span className="font-semibold text-stone-700">{formatCurrency(item.valor)}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-stone-200 px-4 py-2 text-xs text-stone-400 flex items-center gap-4">
          <span><kbd className="px-1.5 py-0.5 bg-stone-100 rounded">↑↓</kbd> navegar</span>
          <span><kbd className="px-1.5 py-0.5 bg-stone-100 rounded">Enter</kbd> selecionar</span>
          <span><kbd className="px-1.5 py-0.5 bg-stone-100 rounded">Esc</kbd> fechar</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
